import { createContext, useContext } from "react";
import generateId from "../../../../util/generateId";
import { getBestVariant } from "./productAdapter";

const SELECT_VARIANT = "select_variant";
const UPDATE_NOTES = "update_notes";
const UPDATE_PERSONALIZATION_TO = "update_personalization_to";
const UPDATE_ADDONS = "update_addons";
const UPDATE_RECIPIENT_EMAIL = "update_recipient_email";
const UPDATE_CUSTOMER_IMAGE = "update_customer_image";
const DISMISS_COA = "dismiss_coa";
const SYNC_COA = "sync_coa";
const ADD_SERVICE = "add_service";
const REMOVE_SERVICE = "remove_service";
const UPDATE_SERVICE_TEXT = "update_service_text";
const CLEAR_SERVICES = "clear_services";

const nextServiceId = () => generateId();

export function createInitialState(product, influencer) {
  let selectedVariant = null;
  if (product?._variants?.length >= 1) {
    // Pre-select the most expensive available variant (falls back to most expensive overall)
    selectedVariant = getBestVariant(product);
  }

  return {
    selectedVariant,
    notes: "",
    personalizationTo: "",
    recipientEmail: "",
    addons: {},
    services: [],
    customerImage: null,
    // When influencer hasn't loaded yet, suppress auto-add until we know
    // the influencer's coa_opt_in preference.
    coaDismissed: influencer ? Boolean(influencer.coa_opt_in) : true,
  };
}

export function reducer(state, action) {
  const { type, payload } = action;

  switch (type) {
    case SELECT_VARIANT:
      return { ...state, selectedVariant: payload, services: [] };
    case DISMISS_COA:
      return { ...state, coaDismissed: true };
    case SYNC_COA:
      return { ...state, coaDismissed: Boolean(payload) };
    case UPDATE_NOTES:
      return { ...state, notes: payload };
    case UPDATE_PERSONALIZATION_TO:
      return { ...state, personalizationTo: payload };
    case UPDATE_ADDONS:
      return { ...state, addons: payload };
    case UPDATE_RECIPIENT_EMAIL:
      return { ...state, recipientEmail: payload };
    case UPDATE_CUSTOMER_IMAGE:
      return { ...state, customerImage: payload };
    case ADD_SERVICE:
      return {
        ...state,
        services: [...state.services, { ...payload, id: nextServiceId() }],
      };
    case REMOVE_SERVICE:
      return {
        ...state,
        services: state.services.filter((s) => s.id !== payload),
      };
    case UPDATE_SERVICE_TEXT:
      return {
        ...state,
        services: state.services.map((s) =>
          s.id === payload.id ? { ...s, customerText: payload.text } : s,
        ),
      };
    case CLEAR_SERVICES:
      return { ...state, services: [] };
    default:
      return state;
  }
}

export const selectVariant = (variant) => ({
  type: SELECT_VARIANT,
  payload: variant,
});
export const updateNotes = (val) => ({ type: UPDATE_NOTES, payload: val });
export const updatePersonalizationTo = (val) => ({
  type: UPDATE_PERSONALIZATION_TO,
  payload: val,
});
export const updateAddons = (val) => ({ type: UPDATE_ADDONS, payload: val });
export const updateRecipientEmail = (val) => ({
  type: UPDATE_RECIPIENT_EMAIL,
  payload: val,
});
export const updateCustomerImage = (val) => ({
  type: UPDATE_CUSTOMER_IMAGE,
  payload: val,
});
export const dismissCoa = () => ({ type: DISMISS_COA });
export const syncCoa = (coaOptIn) => ({ type: SYNC_COA, payload: coaOptIn });

export const addService = (service) => ({
  type: ADD_SERVICE,
  payload: service,
});
export const removeService = (instanceId) => ({
  type: REMOVE_SERVICE,
  payload: instanceId,
});
export const updateServiceText = (instanceId, text) => ({
  type: UPDATE_SERVICE_TEXT,
  payload: { id: instanceId, text },
});
export const clearServices = () => ({ type: CLEAR_SERVICES });

export const ProductPageContext = createContext(null);

export function useProductPageContext() {
  return useContext(ProductPageContext);
}
